import React, { useEffect, useState } from 'react';
import apiClient from '../api/client';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Plus, Edit, Trash2, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import TeamModal from '../components/modals/TeamModal';
import { useAuth } from '../contexts/AuthContext';
import { getCurrencySymbol, formatCurrency } from '../utils/currency';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';

const ITEMS_PER_PAGE = 9;

const Team = () => {
  const { user } = useAuth();
  const [team, setTeam] = useState([]);
  const [filteredTeam, setFilteredTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [memberToDelete, setMemberToDelete] = useState(null);
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);

  const fetchTeam = React.useCallback(async () => {
    try {
      const response = await apiClient.get('/team');
      setTeam(response.data.items || response.data);
    } catch (error) {
      toast.error('Failed to load team members');
    } finally {
      setLoading(false);
    }
  }, []);

  const applyFilters = React.useCallback(() => {
    let filtered = [...team];

    if (searchQuery.trim()) {
      filtered = filtered.filter(member =>
        member.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        member.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
        member.role.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    setFilteredTeam(filtered);
    setCurrentPage(1);
  }, [team, searchQuery]);

  useEffect(() => {
    fetchTeam();
  }, [fetchTeam]);

  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  const getPaginatedTeam = () => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    return filteredTeam.slice(startIndex, endIndex);
  };

  const totalPages = Math.ceil(filteredTeam.length / ITEMS_PER_PAGE);

  const handleAdd = () => {
    setSelectedMember(null);
    setModalOpen(true);
  };

  const handleEdit = (member) => {
    setSelectedMember(member);
    setModalOpen(true);
  };

  const handleDeleteClick = (member) => {
    setMemberToDelete(member);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await apiClient.delete(`/team/${memberToDelete.member_id}`);
      toast.success('Team member deleted successfully');
      fetchTeam();
      setDeleteDialogOpen(false);
      setMemberToDelete(null);
    } catch (error) {
      toast.error('Failed to delete team member');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-6" data-testid="team-page">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Team Members</h1>
            <p className="text-slate-600 mt-1">Manage your team and collaborators</p>
          </div>
          <Button
            onClick={handleAdd}
            className="bg-indigo-600 hover:bg-indigo-700"
            data-testid="add-team-member-button"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Team Member
          </Button>
        </div>

        {/* Search Filter */}
        <Card className="border-slate-100">
          <CardContent className="pt-6">
            <Input
              placeholder="Search by name, email, or role..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="bg-slate-50"
            />
          </CardContent>
        </Card>

        {getPaginatedTeam().length === 0 ? (
          <Card className="border-slate-100">
            <CardContent className="py-12">
              <div className="text-center">
                <p className="text-slate-500 mb-4">No team members found. Start building your team!</p>
                <Button
                  onClick={handleAdd}
                  className="bg-indigo-600 hover:bg-indigo-700"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add First Team Member
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {getPaginatedTeam().map((member) => (
                <Card
                  key={member.member_id}
                  className="border-slate-100 hover:shadow-lg transition-all cursor-pointer group"
                  data-testid={`team-member-${member.member_id}`}
                  onClick={() => window.location.href = `/team/${member.member_id}`}
                >
                  <CardHeader>
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <CardTitle className="text-lg group-hover:text-indigo-600 transition-colors">{member.name}</CardTitle>
                        <p className="text-sm text-slate-500 mt-1">{member.role}</p>
                      </div>
                      <div className="flex space-x-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => { e.stopPropagation(); window.location.href = `/team/${member.member_id}`; }}
                          className="text-indigo-600"
                        >
                          View
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => { e.stopPropagation(); handleEdit(member); }}
                          data-testid={`edit-team-${member.member_id}`}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => { e.stopPropagation(); handleDeleteClick(member); }}
                          className="text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                          data-testid={`delete-team-${member.member_id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="flex justify-between items-start mb-2">
                      <p className="text-sm text-slate-600">{member.email}</p>
                      {member.total_paid !== undefined && (
                        <div className="text-right">
                          <p className="text-[10px] font-bold text-slate-400 uppercase">Total Paid</p>
                          <p className="text-sm font-bold text-emerald-600">{formatCurrency(member.total_paid, user?.currency)}</p>
                        </div>
                      )}
                    </div>
                    {member.cost_per_project && (
                      <p className="text-sm text-slate-500 mt-1">
                        Cost: {formatCurrency(member.cost_per_project, user?.currency)}/project
                      </p>
                    )}
                    {member.skills && member.skills.length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-1">
                        {member.skills.map((skill, idx) => (
                          <span
                            key={idx}
                            className="px-2 py-1 bg-indigo-50 text-indigo-600 text-xs rounded"
                          >
                            {skill}
                          </span>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <Card className="border-slate-100">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-slate-600">
                      Showing {((currentPage - 1) * ITEMS_PER_PAGE) + 1} to {Math.min(currentPage * ITEMS_PER_PAGE, filteredTeam.length)} of {filteredTeam.length} members
                    </p>
                    <div className="flex space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                        disabled={currentPage === 1}
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </Button>
                      <span className="px-4 py-2 text-sm">
                        Page {currentPage} of {totalPages}
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                        disabled={currentPage === totalPages}
                      >
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </div>

      <TeamModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        member={selectedMember}
        onSuccess={fetchTeam}
      />

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete {memberToDelete?.name} from your team. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="bg-rose-600 hover:bg-rose-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default Team;
